// Lightweight inline-SVG icon set to replace emoji-as-UI-icons (❄🌡💧🏆🔒 …),
// which read inconsistently to screen readers and vary across platforms.
// Usage: <Icon name="trophy" /> (decorative) or <Icon name="lock" label="Privé" />.
//
// Icons use stroke=currentColor so they inherit text colour. 24x24 viewBox.
const PATHS = {
  close: <path d="M6 6l12 12M18 6L6 18" />,
  check: <path d="M4 12l5 5L20 6" />,
  chevronLeft: <path d="M15 6l-6 6 6 6" />,
  chevronRight: <path d="M9 6l6 6-6 6" />,
  share: (
    <>
      <path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7" />
      <path d="M12 3v13M8 7l4-4 4 4" />
    </>
  ),
  trophy: (
    <>
      <path d="M7 4h10v4a5 5 0 01-10 0V4z" />
      <path d="M7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3" />
      <path d="M12 13v4M9 21h6M10 17h4" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </>
  ),
  star: (
    <path d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" />
  ),
  eye: (
    <>
      <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M10.6 6.2A9.9 9.9 0 0112 6c6.4 0 10 6 10 6a17 17 0 01-3.3 3.8M6.4 6.5A17 17 0 002 12s3.6 6 10 6a9.6 9.6 0 004-.8" />
      <path d="M9.9 9.9a3 3 0 004.2 4.2" />
      <path d="M3 3l18 18" />
    </>
  ),
  snowflake: (
    <>
      <path d="M12 3v18M3 12h18" />
      <path d="M6 6l12 12M18 6L6 18" />
    </>
  ),
  thermometer: <path d="M12 14V4a2 2 0 00-4 0v10a4 4 0 104 0z" />,
  droplet: <path d="M12 3s6 6.5 6 11a6 6 0 01-12 0c0-4.5 6-11 6-11z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </>
  ),
  wave: <path d="M3 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0" />,
  zap: <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />,
  route: (
    <>
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M8 19h6a4 4 0 000-8H9a4 4 0 010-8h5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </>
  ),
  flame: <path d="M12 3c1 4 4 5 4 9a4 4 0 01-8 0c0-2 1-3 1-3 1 2 3 2 3 0 0-2-1-4 0-6z" />,
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" />
    </>
  ),
  refresh: (
    <>
      <path d="M21 12a9 9 0 11-2.64-6.36L21 8" />
      <path d="M21 3v5h-5" />
    </>
  ),
};

export default function Icon({ name, size = 20, label, strokeWidth = 2, fill = false, className = '', ...rest }) {
  const path = PATHS[name];
  if (!path) return null;
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': 'true' };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...a11y}
      {...rest}
    >
      {path}
    </svg>
  );
}

export const ICON_NAMES = Object.keys(PATHS);
